import type { CSSProperties } from 'react'
import { Download } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { Section, WorkspaceSkeleton } from '../components/UI'
import { useTeamScope } from '../components/TeamScope'
import {
  formatDuration,
  formatRatio,
  loadTodayOperations,
  percentMet,
  type DailyActivity,
  type DailyBenchmarkSummary,
  type DailyOperatingPressure,
  type DailyQueueMetrics,
  type HourPoint,
  type QueuePressure,
  type WorkRecord
} from '../lib/liveOperations'





function simpleReportError(message: string) {
  const text = message.toLowerCase()

  if (text.includes('daily_operating_summary')) {
    return 'Report history is not available yet because the daily report summary could not be loaded.'
  }

  if (text.includes('permission') || text.includes('policy') || text.includes('rls')) {
    return 'Report data is blocked by workspace permissions.'
  }

  if (text.includes('network') || text.includes('fetch')) {
    return 'Report data could not be reached. Check the connection and refresh.'
  }

  if (text.includes('does not exist') || text.includes('not found')) {
    return 'Part of the reporting data is not available in the database yet.'
  }

  return message || 'Report data could not be loaded.'
}

export function Reports() {
  const { selectedTeamId } = useTeamScope()
  const [records, setRecords] = useState<WorkRecord[]>([])
  const [daily, setDaily] = useState<DailyActivity | null>(null)
  const [queueMetrics, setQueueMetrics] = useState<DailyQueueMetrics | null>(null)
  const [benchmarkSummary, setBenchmarkSummary] = useState<DailyBenchmarkSummary | null>(null)
  const [queuePressure, setQueuePressure] = useState<QueuePressure | null>(null)
  const [operatingPressure, setOperatingPressure] = useState<DailyOperatingPressure | null>(null)
  const [hourly, setHourly] = useState<HourPoint[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    setLoading(true)
    setError('')
    loadTodayOperations(selectedTeamId)
      .then(data => {
        setRecords(data.records)
        setDaily(data.daily)
        setQueueMetrics(data.queueMetrics)
        setBenchmarkSummary(data.benchmarkSummary)
        setQueuePressure(data.queuePressure)
        setOperatingPressure(data.operatingPressure)
        setHourly(data.hourly)
      })
      .catch(err => setError(simpleReportError(err instanceof Error ? err.message : String(err))))
      .finally(() => setLoading(false))
  }, [selectedTeamId])

  const replied = daily?.threads_replied ?? records.filter(record => record.work_status === 'replied').length
  const completion = records.length ? Math.round((replied / records.length) * 100) : 0

  const replySentMet = percentMet(
    benchmarkSummary?.reply_sent_target_met_count ?? 0,
    benchmarkSummary?.reply_sent_target_evaluated_count ?? 0
  )

  const peak = useMemo(() => {
    if (!hourly.length) return null
    return [...hourly].sort((a, b) => (b.opened + b.replied) - (a.opened + a.replied))[0]
  }, [hourly])

  if (loading) return <WorkspaceSkeleton rows={4}/>

  return <div className="stack-lg reports-clean">
    {error && <div className="notice notice-inline"><strong>Report unavailable:</strong><span>{error}</span></div>}

    <>
      <Section
        title="Today’s report"
        subtitle={`${new Date().toLocaleDateString(undefined, { weekday:'long', day:'numeric', month:'long' })} · SLA Performance shown alongside workload and customer email pressure`}
        action={<button className="button secondary" onClick={() => window.print()}><Download size={16}/>Export PDF</button>}
      >
        <div className="report-kpi-grid">
          <div>
            <span>Customer emails received</span>
            <strong>{operatingPressure?.inbound_threads ?? 0}</strong>
            <small>New customer email conversations today</small>
          </div>

          <div>
            <span>Customer emails per active employee</span>
            <strong>{operatingPressure?.inbound_threads_per_active_person ?? '—'}</strong>
            <small>{operatingPressure?.active_people ?? 0} active employees measured</small>
          </div>

          <div>
            <span>Response SLA met</span>
            <strong>{replySentMet == null ? '—' : `${replySentMet}%`}</strong>
            <small>Customer email received to first reply sent</small>
          </div>

          <div>
            <span>Average response time</span>
            <strong>{formatDuration(queueMetrics?.average_arrival_to_reply_sent_seconds)}</strong>
            <small>Across customer emails with a measured reply</small>
          </div>
        </div>
      </Section>

      <div className="report-grid report-grid-balanced">
        <Section title="Customer response completion" subtitle="How many measured customer emails received a reply">
          <div className="service-pie-wrap">
            <div
              className="service-pie"
              style={{'--pie-value': `${completion}%`} as CSSProperties}
              role="img"
              aria-label={`${completion}% of measured customer emails received a reply`}
            >
              <div className="service-pie-center">
                <strong>{completion}%</strong>
                <span>replied</span>
              </div>
            </div>

            <div className="service-pie-legend">
              <div><i className="pie-dot pie-dot-primary"/><span><strong>{replied}</strong> measured customer emails with a reply sent</span></div>
              <div><i className="pie-dot pie-dot-muted"/><span><strong>{records.length - replied}</strong> still open or in progress</span></div>
              <p>Reply volume and SLA results are shown separately so one does not disguise the other.</p>
            </div>
          </div>
        </Section>

        <Section title="Customer email pressure" subtitle="The workload conditions behind today’s response result">
          <div className="context-grid report-context-grid">
            <div><span>Waiting now</span><strong>{queuePressure?.waiting_threads ?? 0}</strong></div>
            <div><span>Longest current wait</span><strong>{formatDuration(queuePressure?.oldest_current_wait_seconds)}</strong></div>
            <div><span>Replies sent vs emails received</span><strong>{formatRatio(operatingPressure?.reply_output_to_inbound_ratio)}</strong></div>
            <div><span>Average response time</span><strong>{formatDuration(queueMetrics?.average_arrival_to_reply_sent_seconds)}</strong></div>
          </div>
        </Section>
      </div>

      <Section title="Accountability context" subtitle="Read performance alongside the conditions your team was working under">
        <div className="report-insight-grid">
          <div><span>Customer emails received</span><strong>{operatingPressure?.inbound_threads ?? 0}</strong><small>New customer email conversations received today.</small></div>
          <div><span>Employees active today</span><strong>{operatingPressure?.active_people ?? 0}</strong><small>People with measured customer-email activity today.</small></div>
          <div><span>Customer emails waiting</span><strong>{queuePressure?.waiting_threads ?? 0}</strong><small>SLA misses should be read alongside these workload conditions.</small></div>
        </div>
      </Section>

      <Section title="Today’s busiest customer-email hour" subtitle="The busiest measured hour for customer-email activity">
        <div className="report-insight-grid">
          <div><span>Busiest hour</span><strong>{peak?.label ?? '—'}</strong><small>Based on customer emails received and replies sent.</small></div>
          <div><span>Customer emails received</span><strong>{peak?.opened ?? 0}</strong><small>Distinct customer email conversations received in that hour.</small></div>
          <div><span>Replies sent</span><strong>{peak?.replied ?? 0}</strong><small>Distinct measured customer emails replied to in that hour.</small></div>
        </div>
      </Section>
    </>
  </div>
}
